/**
 * Global throttle/debounce utilities for rate limiting
 */

// Per-user message tracking (5 messages per minute max)
const userMessageTimestamps = new Map<string, number[]>();
const MAX_MESSAGES_PER_MINUTE = 5;
const MINUTE_MS = 60000;
const MIN_INTERVAL_MS = 12000; // 12 seconds minimum between sends (5 per min)

// Circuit breaker for 429 errors
let circuitBreakerOpen = false;
let circuitBreakerOpenUntil = 0;
const CIRCUIT_BREAKER_DURATION = 60000; // 1 minute cooldown after 429

/**
 * Generic throttle implementation for rate limiting
 * @param maxCalls - Maximum number of calls allowed
 * @param windowMs - Time window in milliseconds
 * @returns Throttle function that returns true if allowed
 */
function createThrottle(maxCalls: number, windowMs: number) {
  const timestamps: number[] = [];

  return {
    canProceed(): boolean {
      const now = Date.now();

      // Remove expired timestamps
      while (timestamps.length > 0 && now - timestamps[0] > windowMs) {
        timestamps.shift();
      }

      // Check if at limit
      if (timestamps.length >= maxCalls) {
        return false;
      }

      // Add timestamp
      timestamps.push(now);
      return true;
    },
    reset(): void {
      timestamps.length = 0;
    },
    getRemaining(): number {
      const now = Date.now();
      if (timestamps.length === 0) return 0;

      // Remove expired timestamps
      while (timestamps.length > 0 && now - timestamps[0] > windowMs) {
        timestamps.shift();
      }

      if (timestamps.length < maxCalls) return 0;

      const oldestTimestamp = timestamps[0];
      return Math.max(0, windowMs - (now - oldestTimestamp));
    },
  };
}

/**
 * Pre-configured rate limiters for different services
 */
export const rateLimiters = {
  // Fitbit sync: 10 requests per minute
  fitbitSync: createThrottle(10, 60000),

  // Health data queries: 10 requests per minute
  healthDataQuery: createThrottle(10, 60000),

  // Profile updates: 5 requests per minute
  profileUpdate: createThrottle(5, 60000),

  // Generic API calls: 20 requests per minute
  apiCall: createThrottle(20, 60000),
};

/**
 * Throttle chat sends globally to prevent rate limiting (429 errors)
 * Enforces 5 messages per minute per user with 12s minimum interval
 * @param userId - User identifier for per-user throttling
 * @returns true if allowed to send, false if throttled
 */
export function canSendChatMessage(userId: string = 'default'): boolean {
  const now = Date.now();

  // Check circuit breaker
  if (circuitBreakerOpen) {
    if (now < circuitBreakerOpenUntil) {
      return false;
    }
    // Reset circuit breaker
    circuitBreakerOpen = false;
    circuitBreakerOpenUntil = 0;
  }

  // Get user's message timestamps
  let timestamps = userMessageTimestamps.get(userId) || [];

  // Remove timestamps older than 1 minute
  timestamps = timestamps.filter((ts) => now - ts < MINUTE_MS);

  // Check if at rate limit (5 messages in last minute)
  if (timestamps.length >= MAX_MESSAGES_PER_MINUTE) {
    return false;
  }

  // Check minimum interval (12s between messages)
  if (timestamps.length > 0) {
    const lastTimestamp = timestamps[timestamps.length - 1];
    if (now - lastTimestamp < MIN_INTERVAL_MS) {
      return false;
    }
  }

  // Add current timestamp and update
  timestamps.push(now);
  userMessageTimestamps.set(userId, timestamps);

  return true;
}

/**
 * Reset chat throttle for a user (for testing or manual reset)
 */
export function resetChatThrottle(userId: string = 'default'): void {
  userMessageTimestamps.delete(userId);
}

/**
 * Get remaining throttle time in ms for next message
 */
export function getChatThrottleRemaining(userId: string = 'default'): number {
  const now = Date.now();

  // Check circuit breaker first
  if (circuitBreakerOpen && now < circuitBreakerOpenUntil) {
    return circuitBreakerOpenUntil - now;
  }

  const timestamps = userMessageTimestamps.get(userId) || [];
  if (timestamps.length === 0) return 0;

  const lastTimestamp = timestamps[timestamps.length - 1];
  const remaining = MIN_INTERVAL_MS - (now - lastTimestamp);
  return Math.max(0, remaining);
}

/**
 * Open circuit breaker after 429 error
 */
export function openCircuitBreaker(): void {
  circuitBreakerOpen = true;
  circuitBreakerOpenUntil = Date.now() + CIRCUIT_BREAKER_DURATION;
}

/**
 * Get messages sent in last minute for a user
 */
export function getMessageCount(userId: string = 'default'): number {
  const now = Date.now();
  const timestamps = userMessageTimestamps.get(userId) || [];
  return timestamps.filter((ts) => now - ts < MINUTE_MS).length;
}

/** @internal Reset all module-level throttle state. For testing only. */
export function _resetAllForTesting(): void {
  userMessageTimestamps.clear();
  circuitBreakerOpen = false;
  circuitBreakerOpenUntil = 0;
}
